<template>
    <div>
        <form-modal
            ref="formModal"
            :title="modalTitle"
            size="lg"
            :in-editing="isEdit"
            :handle-close="handleClose"
        >
            <template v-slot:form>
                <div
                    v-if="existingRecord"
                    class="loja-perfeita-modal-toolbar d-flex justify-content-end align-items-center flex-nowrap w-100 mb-2"
                >
                    <edit-label-button
                        v-if="!isEdit"
                        :title="getI18n(i18nKey, 'TITLES.criterio')"
                        :disabled="submitLoading"
                        @onClick="enterEditMode"
                    />
                    <b-button
                        class="btn btn-pdv btn-pdv-blue"
                        type="button"
                        :disabled="submitLoading"
                        @click="emitOpenQuestions"
                    >
                        {{ getI18n(i18nKey, 'BUTTONS.questoes') }}
                    </b-button>
                    <delete-label-button
                        :title="getI18n(i18nKey, 'TITLES.criterio')"
                        :disabled="submitLoading"
                        @onClick="confirmDeleteCriterion"
                    />
                </div>
                <b-tabs
                    v-model="criterionTabIndex"
                    class="tabs-pdv"
                >
                    <b-tab :title="getI18n(i18nKey, 'TITLES.criterio')">
                        <form @submit.prevent="handleSubmit">
                            <b-row>
                                <b-col md="12">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.nome')"
                                        label-for="criterion-name"
                                        class="required label-pdv"
                                    >
                                        <b-form-input
                                            id="criterion-name"
                                            v-model.trim="formData.name"
                                            class="input-pdv-blue"
                                            type="text"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.tipo_regra')"
                                        label-for="criterion-rule-type"
                                        class="label-pdv"
                                    >
                                        <b-form-select
                                            id="criterion-rule-type"
                                            v-model="formData.rule_type"
                                            class="input-pdv-blue"
                                            :options="ruleTypeOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.pontuacao')"
                                        label-for="criterion-weight"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            id="criterion-weight"
                                            v-model.number="formData.weight"
                                            class="input-pdv-blue"
                                            type="number"
                                            min="0"
                                            step="any"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row v-if="formData.rule_type === 'dependency' || formData.rule_type === 'comparative'">
                                <b-col md="12">
                                    <b-alert show variant="light" class="border small mb-2">
                                        <strong class="d-block mb-1">{{ getI18n(i18nKey, 'TITLES.criterion_dependency_block') }}</strong>
                                        <span class="text-muted">{{ getI18n(i18nKey, 'INTROS.criterion_dependency_cross') }}</span>
                                    </b-alert>
                                </b-col>
                            </b-row>
                            <b-row v-if="formData.rule_type === 'dependency' || formData.rule_type === 'comparative'">
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'FIELDS.depends_on_criterion')"
                                        label-for="criterion-depends-on"
                                        class="label-pdv"
                                    >
                                        <b-form-select
                                            id="criterion-depends-on"
                                            v-model="formData.depends_on_criterion_id"
                                            class="input-pdv-blue"
                                            :options="dependsOnOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        >
                                            <template v-slot:first>
                                                <b-form-select-option :value="null">{{ getI18n('COMMON', 'none') || 'Nenhum' }}</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'FIELDS.when_dependency_missing')"
                                        label-for="criterion-when-missing"
                                        class="label-pdv"
                                    >
                                        <b-form-select
                                            id="criterion-when-missing"
                                            v-model="formData.when_dependency_missing"
                                            class="input-pdv-blue"
                                            :options="whenMissingOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="d-flex justify-content-end gap-2 mt-3">
                                <b-button class="btn btn-pdv-gradient-blue-transparent" :disabled="submitLoading" @click="handleClose">
                                    {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                                </b-button>
                                <b-button
                                    class="btn-pdv-gradient-blue ml-3"
                                    type="submit"
                                    :disabled="submitLoading || !formData.name || !isFormEditable"
                                >
                                    <b-spinner v-if="submitLoading" small class="mr-1" />
                                    {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                                </b-button>
                            </div>
                        </form>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.segmentacao')"
                        :disabled="!existingRecord"
                    >
                        <div class="mt-2">
                            <loja-perfeita-conditions-editor
                                v-if="existingRecord"
                                ref="criterionConditionsEditor"
                                parent-type="criterion"
                                :parent-id="formData.id"
                                :hint="getI18n(i18nKey, 'MESSAGES.segmentacao_hint_criterio')"
                            />
                        </div>
                    </b-tab>
                </b-tabs>
            </template>
        </form-modal>

        <b-modal
            ref="deleteConfirmModal"
            centered
            size="md"
            @hidden="deleteConfirmCallback = null"
        >
            <template v-slot:modal-header>
                <h4 class="modal-title description">
                    {{ deleteConfirmTitle }}
                </h4>
                <button type="button" class="close" aria-label="Close" @click="closeDeleteConfirm">
                    <i class="fe-x-circle icon-blue" aria-hidden="true"></i>
                </button>
            </template>
            <div class="message">
                {{ deleteConfirmBody }}
            </div>
            <template v-slot:modal-footer>
                <div class="col text-center ml-3 mr-3">
                    <delete-button
                        class="mb-1"
                        :busy="deleteConfirmBusy"
                        :title="getI18n(i18nKey, 'BUTTONS.excluir')"
                        @onClick="executeDeleteConfirm"
                    />
                    <cancel-label-button @onClick="closeDeleteConfirm" />
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import FormModal from '@src/components/FormModal.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import DeleteButton from '@src/components/DeleteButton.vue'
import CancelLabelButton from '@src/components/CancelLabelButton.vue'
import LojaPerfeitaService, { LojaPerfeitaServiceClass } from '@src/services/dashboards/LojaPerfeitaService'
import LojaPerfeitaConditionsEditor from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaConditionsEditor.vue'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'

const i18nKey = 'LOJA_PERFEITA'
const RULE_TYPES = LojaPerfeitaServiceClass.RULE_TYPES
const WHEN_MISSING = LojaPerfeitaServiceClass.WHEN_DEPENDENCY_MISSING

const defaultForm = () => ({
    id: 0,
    pillar_id: null,
    name: '',
    rule_type: RULE_TYPES.binary,
    weight: 0,
    depends_on_criterion_id: null,
    when_dependency_missing: WHEN_MISSING.exclude,
})

export default {
    components: {
        FormModal,
        EditLabelButton,
        DeleteLabelButton,
        DeleteButton,
        CancelLabelButton,
        LojaPerfeitaConditionsEditor,
    },
    mixins: [Shared, swalFeedback],
    props: {
        campaignId: { type: [String, Number], required: true },
        /** Current pillar from parent modal; criterion create/edit always stays in this pillar. */
        pillarId: { type: [String, Number], required: true },
        criteriaForDependsOn: { type: Array, default: () => [] },
    },
    data() {
        return {
            i18nKey,
            isEdit: false,
            submitLoading: false,
            criterionTabIndex: 0,
            formData: defaultForm(),
            deleteConfirmTitle: '',
            deleteConfirmBody: '',
            deleteConfirmCallback: null,
            deleteConfirmBusy: false,
        }
    },
    computed: {
        existingRecord() {
            return Boolean(this.formData.id && Number(this.formData.id) > 0)
        },
        isFormEditable() {
            return !this.existingRecord || this.isEdit
        },
        modalTitle() {
            return this.formData.id
                ? this.getI18n(i18nKey, 'BUTTONS.editar_criterio')
                : this.getI18n(i18nKey, 'BUTTONS.adicionar_criterio')
        },
        ruleTypeOptions() {
            return [
                { value: RULE_TYPES.binary, text: this.getI18n(i18nKey, 'RULE_TYPES.binary') },
                { value: RULE_TYPES.binary_all, text: this.getI18n(i18nKey, 'RULE_TYPES.binary_all') },
                { value: RULE_TYPES.proportional, text: this.getI18n(i18nKey, 'RULE_TYPES.proportional') },
                { value: RULE_TYPES.dependency, text: this.getI18n(i18nKey, 'RULE_TYPES.dependency') },
                { value: RULE_TYPES.comparative, text: this.getI18n(i18nKey, 'RULE_TYPES.comparative') },
            ]
        },
        whenMissingOptions() {
            return [
                { value: WHEN_MISSING.exclude, text: this.getI18n(i18nKey, 'WHEN_DEPENDENCY_MISSING.exclude') },
                { value: WHEN_MISSING.zero, text: this.getI18n(i18nKey, 'WHEN_DEPENDENCY_MISSING.zero') },
            ]
        },
        dependsOnOptions() {
            return this.criteriaForDependsOn
                .filter((c) => c.id !== this.formData.id)
                .map((c) => ({ value: c.id, text: `${c.pillar_name || ''} — ${c.name}` }))
        },
    },
    watch: {
        criterionTabIndex(val) {
            if (val === 1 && this.existingRecord) {
                this.loadCriterionConditions()
            }
        },
    },
    methods: {
        loadCriterionConditions() {
            if (!this.existingRecord) return
            if (this.$refs.criterionConditionsEditor) {
                this.$refs.criterionConditionsEditor.loadConditions()
                this.$refs.criterionConditionsEditor.loadDimensions()
            }
        },
        enterEditMode() {
            this.isEdit = true
            this.$nextTick(() => {
                const el = document.getElementById('criterion-name')
                if (el) el.focus()
            })
        },
        emitOpenQuestions() {
            if (!this.formData.id) return
            this.$emit('open-questions', { ...this.formData })
        },
        openDeleteConfirm(title, body, callback) {
            this.deleteConfirmTitle = title
            this.deleteConfirmBody = body
            this.deleteConfirmCallback = callback
            this.deleteConfirmBusy = false
            this.$refs.deleteConfirmModal.show()
        },
        closeDeleteConfirm() {
            this.$refs.deleteConfirmModal.hide()
            this.deleteConfirmCallback = null
        },
        executeDeleteConfirm() {
            if (!this.deleteConfirmCallback) return
            this.deleteConfirmBusy = true
            Promise.resolve(this.deleteConfirmCallback())
                .then(() => {
                    this.closeDeleteConfirm()
                })
                .catch((err) => {
                    this.negativeFeedback(this.getI18n('RESPONSES', 'something_wrong'), err?.message)
                })
                .finally(() => {
                    this.deleteConfirmBusy = false
                })
        },
        confirmDeleteCriterion() {
            if (!this.formData.id) return
            const id = this.formData.id
            this.openDeleteConfirm(
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_criterio_title'),
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_criterio'),
                () => LojaPerfeitaService.deleteCriterion(id).then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.criterio'), this.getI18n(i18nKey, 'MESSAGES.config_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
            )
        },
        showModal() {
            this.submitLoading = false
            this.$refs.formModal.show()
        },
        replaceModalData(data) {
            this.formData = { ...defaultForm(), ...data }
            this.formData.pillar_id = Number(this.pillarId)
            this.criterionTabIndex = 0
            this.isEdit = !this.existingRecord
        },
        handleClose() {
            this.$refs.formModal.hide()
        },
        handleSubmit() {
            if (!this.formData.name || this.submitLoading || !this.isFormEditable) return
            this.submitLoading = true
            const pillarId = Number(this.pillarId)
            if (!pillarId) {
                this.negativeFeedback(
                    this.getI18n('RESPONSES', 'something_wrong'),
                    this.getI18n('RESPONSES', 'operation_not_possible')
                )
                this.submitLoading = false
                return
            }
            const payload = {
                name: this.formData.name,
                rule_type: this.formData.rule_type,
                weight: Number(this.formData.weight) || 0,
                depends_on_criterion_id: this.formData.depends_on_criterion_id || null,
                when_dependency_missing: this.formData.when_dependency_missing,
            }
            const promise = this.formData.id
                ? LojaPerfeitaService.updateCriterion(this.formData.id, payload)
                : LojaPerfeitaService.createCriterion(pillarId, payload)
            promise
                .then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.criterio'), this.getI18n(i18nKey, 'MESSAGES.criterio_salvo'))
                    this.handleClose()
                    this.$emit('refresh')
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || this.getI18n('RESPONSES', 'operation_not_possible')
                    )
                })
                .finally(() => { this.submitLoading = false })
        },
    },
}
</script>

<style scoped>
.loja-perfeita-modal-toolbar {
    gap: 0.5rem;
    overflow-x: auto;
}
</style>
